import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Hash, Info, Copy, RotateCcw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const FactorialCalculator: React.FC = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<string>('');
  const [history, setHistory] = useState<Array<{ number: number; factorial: string; time: string }>>([]);

  useSEO({
    title: 'Factorial Calculator - Calculate n! Online',
    description: 'Free online factorial calculator. Calculate factorials of any positive integer instantly with detailed results and calculation history.',
    keywords: 'factorial calculator, n factorial, math calculator, factorial function, mathematics tool',
    canonical: 'https://snap-tools.online/calc/factorial-calculator'
  });

  const calculateFactorial = (n: number): string => {
    if (n < 0) return 'Invalid: Negative numbers';
    if (!Number.isInteger(n)) return 'Invalid: Must be a whole number';
    if (n > 170) return 'Too large: Result would exceed JavaScript limits';
    
    if (n === 0 || n === 1) return '1';
    
    // For large numbers, use BigInt
    if (n > 20) {
      let result = BigInt(1);
      for (let i = 2; i <= n; i++) {
        result *= BigInt(i);
      }
      return result.toString();
    }
    
    // For smaller numbers, use regular calculation
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result.toString();
  };

  const getFactorialSteps = (n: number): string => {
    if (n < 0 || !Number.isInteger(n) || n > 20) return '';
    if (n === 0 || n === 1) return `${n}! = 1`;
    
    const steps = [];
    for (let i = n; i >= 1; i--) {
      steps.push(i.toString());
    }
    return `${n}! = ${steps.join(' × ')} = ${calculateFactorial(n)}`;
  };

  const handleCalculate = () => {
    const num = parseInt(input);
    if (isNaN(num)) {
      toast.error('Please enter a valid number');
      return;
    }

    const factorial = calculateFactorial(num);
    setResult(factorial);
    
    if (!factorial.startsWith('Invalid') && !factorial.startsWith('Too large')) {
      const historyEntry = {
        number: num,
        factorial,
        time: new Date().toLocaleTimeString()
      };
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    }
  };

  const copyResult = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      toast.success('Result copied to clipboard');
    }
  };

  const clearHistory = () => {
    setHistory([]);
    toast.success('History cleared');
  };

  const useFromHistory = (num: number) => {
    setInput(num.toString());
    setResult(calculateFactorial(num));
  };

  useEffect(() => {
    if (input && !isNaN(parseInt(input))) {
      handleCalculate();
    } else {
      setResult('');
    }
  }, [input]);

  const commonFactorials = [
    { n: 0, value: '1' },
    { n: 1, value: '1' },
    { n: 2, value: '2' },
    { n: 3, value: '6' },
    { n: 4, value: '24' },
    { n: 5, value: '120' },
    { n: 6, value: '720' },
    { n: 7, value: '5,040' },
    { n: 8, value: '40,320' },
    { n: 9, value: '362,880' },
    { n: 10, value: '3,628,800' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Hash className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Factorial Calculator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Calculate factorials (n!) of any positive integer. Get instant results with calculation 
            steps and maintain a history of your calculations.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calculator */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Calculate Factorial</h2>
              
              {/* Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter a number (n)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter a positive integer"
                    min="0"
                    max="170"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                    !
                  </div>
                </div>
              </div>

              {/* Result */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Result</label>
                    {!result.startsWith('Invalid') && !result.startsWith('Too large') && (
                      <button
                        onClick={copyResult}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      >
                        <Copy className="h-3 w-3" />
                        Copy
                      </button>
                    )}
                  </div>
                  <div className={`p-4 rounded-lg font-mono text-lg break-all ${
                    result.startsWith('Invalid') || result.startsWith('Too large') 
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : 'bg-green-50 border border-green-200 text-green-700'
                  }`}>
                    {input && !isNaN(parseInt(input)) ? `${input}! = ${result}` : result}
                  </div>
                </motion.div>
              )}

              {/* Calculation Steps */}
              {input && !isNaN(parseInt(input)) && parseInt(input) <= 20 && parseInt(input) >= 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calculation Steps
                  </label>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-mono text-sm text-blue-800 break-all">
                      {getFactorialSteps(parseInt(input))}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Information */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">About Factorials:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• The factorial of n (written as n!) is the product of all positive integers less than or equal to n</li>
                      <li>• By definition, 0! = 1</li>
                      <li>• Factorials grow very quickly (10! = 3,628,800)</li>
                      <li>• Maximum calculable value is 170! due to JavaScript number limits</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Common Factorials */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <h3 className="font-semibold text-gray-900 mb-4">Common Factorials</h3>
              <div className="space-y-2">
                {commonFactorials.map((item) => (
                  <button
                    key={item.n}
                    onClick={() => useFromHistory(item.n)}
                    className="w-full text-left p-2 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-sm">{item.n}!</span>
                      <span className="text-sm text-gray-600">{item.value}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Recent Calculations</h3>
                {history.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                    title="Clear history"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No calculations yet</p>
                ) : (
                  history.map((entry, index) => (
                    <button
                      key={index}
                      onClick={() => useFromHistory(entry.number)}
                      className="w-full text-left p-2 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <div className="font-mono text-sm text-gray-900 break-all">
                        {entry.number}! = {entry.factorial.length > 20 ? entry.factorial.slice(0, 20) + '...' : entry.factorial}
                      </div>
                      <div className="text-xs text-gray-500">{entry.time}</div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default FactorialCalculator;
